import type { LayerModel, VectorLayerModel } from '../core/types';
import { buildLayerModel } from '../domain/hierarchy';

const normalizedField = (value: string): string => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '').toLocaleLowerCase();
const aliases = {
  finca: ['codfinca', 'codigofinca'],
  suerte: ['codsuerte', 'codigosuerte', 'suerte'],
  tablon: ['tablon', 'codtablon'],
};

export interface MasterEligibility {
  readonly eligible: boolean;
  readonly reason: string;
}

export function masterEligibility(layer: VectorLayerModel): MasterEligibility {
  if (layer.kind === 'working') return { eligible: false, reason: 'Una capa de trabajo no puede convertirse en Maestro.' };
  if (!layer.features.length) return { eligible: false, reason: 'La capa no contiene geometrías.' };
  if (layer.geometryTypes.some((type) => type !== 'Polygon' && type !== 'MultiPolygon')) {
    return { eligible: false, reason: 'El Maestro CASUR requiere geometrías poligonales.' };
  }
  const fields = new Set(layer.attributeFields.map(normalizedField));
  const missing = Object.entries(aliases).filter(([, names]) => !names.some((name) => fields.has(name))).map(([name]) => name);
  if (missing.length) {
    return { eligible: false, reason: `Faltan campos para reconstruir la jerarquía: ${missing.join(', ')}.` };
  }
  return { eligible: true, reason: 'La capa contiene geometría y claves Finca/Suerte/Tablón.' };
}

export function masterModelFromVectorLayer(layer: VectorLayerModel): LayerModel {
  const eligibility = masterEligibility(layer);
  if (!eligibility.eligible) throw new Error(eligibility.reason);
  return buildLayerModel(
    layer.sourceName,
    layer.sourceFormat as Exclude<VectorLayerModel['sourceFormat'], 'WORK'>,
    layer.features.map((record, sourceIndex) => {
      const feature = record.feature.clone();
      feature.unset('layerId', true);
      feature.unset('genericFeatureId', true);
      return { feature, attributes: record.attributes, sourceIndex };
    }),
    layer.sourceCrs,
  );
}
